/* eslint-disable max-len */
import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Avatar,
} from "@mui/material";
import { Button as MuiButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled, { keyframes } from "styled-components";
import { SemesterContext } from "../context/SemesterContext";
import { UserContext } from "../context/UserContext";
import { UniversityContext } from "../context/UniversityContext";
import { handleJWTError } from "../hooks/jwtErrHandler";

interface ApprovalRecord {
  id: string;
  cmkl_id: string;
  student_id: string;
  student_name: string;
  nickname: string;
  email: string;
  competency_code: string;
  title: string;
  credits: number;
  display_version: string;
  status_val: number;
  status: string;
  status_changed_date?: string;
  comments: string;
  assessed_skills: number;
  total_skills: number;
  average_score: number;
}

interface LocalState {
  status_val: number;
  comments: string;
  dirty: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaveComplete?: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const StudentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: background 0.2s;
  animation: ${fadeIn} 0.3s ease forwards;
  &:hover { background: rgba(255, 255, 255, 0.07); }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 12px;
  color: #e2e8f0;
  white-space: nowrap;
`;

const ScoreChip = styled(StatChip)<{ score: number }>`
  border-color: ${({ score }) => score >= 80 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444"};
  color: ${({ score }) => score >= 80 ? "#86efac" : score >= 60 ? "#fcd34d" : "#fca5a5"};
`;

const RadioLabel = styled.span`
  font-size: 13px;
  color: #e2e8f0;
`;

export default function AORequestsModal({ open, onClose, onSaveComplete }: Props) {
  const userContext = useContext(UserContext);
  const semesterContext = useContext(SemesterContext);
  const universityContext = useContext(UniversityContext);

  const [records, setRecords] = useState<ApprovalRecord[]>([]);
  const [localStates, setLocalStates] = useState<Record<string, LocalState>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [approvalStatuses, setApprovalStatuses] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      axios
        .get("/approval-status")
        .then((res) => {
          setApprovalStatuses(res.data.approval_status_types || []);
        })
        .catch((err) => console.error("Error fetching approval statuses:", err));
    }
  }, [open]);

  const fetchRecords = useCallback(async () => {
    const semesterId = semesterContext.selectedSemester?.value;
    const programId = universityContext.selectedProgram?.id;
    const instructorId = userContext.user?.id;
    if (!semesterId || !programId || !instructorId) return;
    setLoading(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await axios.get(
        `/instructor/pending-approval?instructor_id=${instructorId}&semester_id=${semesterId}&program_id=${programId}`
      );
      const data: ApprovalRecord[] = Array.isArray(res.data) ? res.data : [];
      setRecords(data);
      const init: Record<string, LocalState> = {};
      data.forEach((r) => {
        init[r.id] = { status_val: r.status_val || 1, comments: r.comments || "", dirty: false };
      });
      setLocalStates(init);
    } catch (err) {
      handleJWTError(err);
    } finally {
      setLoading(false);
    }
  }, [semesterContext.selectedSemester?.value, universityContext.selectedProgram?.id, userContext.user?.id]);

  useEffect(() => { if (open) fetchRecords(); }, [open, fetchRecords]);

  const setStatus = (id: string, val: number) =>
    setLocalStates((prev) => ({ ...prev, [id]: { ...prev[id], status_val: val, dirty: true } }));

  const setComment = (id: string, val: string) =>
    setLocalStates((prev) => ({ ...prev, [id]: { ...prev[id], comments: val, dirty: true } }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    const dirtyIds = Object.entries(localStates).filter(([, v]) => v.dirty).map(([id]) => id);
    if (dirtyIds.length === 0) { setSaving(false); setSaveSuccess(true); return; }

    const updatesPayload = dirtyIds.map((id) => ({
      approval_id: id,
      status: localStates[id].status_val,
      comments: localStates[id].comments,
    }));

    try {
      const response = await axios.put("/instructor/pending-approval/update", {
        updates: updatesPayload,
      });

      const { success, message, results } = response.data;

      if (success) {
        setSaveSuccess(true);
        setLocalStates((prev) => {
          const next = { ...prev };
          dirtyIds.forEach((id) => (next[id] = { ...next[id], dirty: false }));
          return next;
        });
        onSaveComplete?.();
        onClose();
      } else {
        setSaveError(message || "Some updates failed. Please review the errors.");
        if (results && Array.isArray(results)) {
          setLocalStates((prev) => {
            const next = { ...prev };
            results.forEach((res: any) => {
              if (res.success && next[res.approval_id]) {
                next[res.approval_id] = { ...next[res.approval_id], dirty: false };
              }
            });
            return next;
          });
        }
      }
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || err?.response?.data?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (val: number) => val === 1 ? "#f59e0b" : val === 2 ? "#22c55e" : "#ef4444";
  const dirtyCount = Object.values(localStates).filter((v) => v.dirty).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 16,
          color: "#e2e8f0",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          padding: "16px 24px",
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9" }}>AO Competency Requests</div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>
            {semesterContext.selectedSemester?.label} &middot; {records.length} student{records.length !== 1 ? "s" : ""} pending review
          </div>
        </div>
        <IconButton onClick={onClose} size="small" style={{ color: "#94a3b8" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: "16px 24px", overflowY: "auto" }} className="visible-scrollbar">
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <CircularProgress style={{ color: "#d97706" }} />
          </div>
        ) : records.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
            No pending AO approval requests for this semester.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {records.map((r, idx) => {
              const ls = localStates[r.id] ?? { status_val: 1, comments: "", dirty: false };
              const avgPct = Math.round(r.average_score * 10) / 10;
              return (
                <StudentRow key={r.id}>
                  <InfoRow>
                    <Avatar style={{ background: `hsl(${(idx * 47) % 360}, 55%, 40%)`, width: 40, height: 40, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                      {r.student_name.charAt(0)}
                    </Avatar>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#f1f5f9" }}>
                        {r.student_name}
                        {r.nickname && <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>"{r.nickname}"</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{r.email}</div>
                    </div>
                    <Chip
                      label={`${r.competency_code}:${r.display_version}`}
                      size="small"
                      style={{ background: "rgba(255,255,255,0.06)", color: "#cbd5e1", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11, maxWidth: 260 }}
                    />
                  </InfoRow>

                  <InfoRow>
                    <StatChip>Skills: <strong>{r.total_skills}</strong> / {r.assessed_skills} assessed</StatChip>
                    <ScoreChip score={avgPct}>Avg Score: <strong>{avgPct.toFixed(1)}</strong></ScoreChip>
                    <StatChip>Credits: {r.credits}</StatChip>
                    <StatChip>Version: {r.display_version}</StatChip>
                    {ls.dirty && <Chip label="Unsaved" size="small" style={{ background: "#78350f", color: "#fde68a", fontSize: 10 }} />}
                  </InfoRow>

                  <Divider style={{ borderColor: "rgba(255,255,255,0.08)" }} />

                  <RadioGroup row value={ls.status_val} onChange={(e) => setStatus(r.id, Number(e.target.value))}>
                    {approvalStatuses.map((st) => {
                      const color = statusColor(st.value);
                      return (
                        <FormControlLabel
                          key={st.value}
                          value={st.value}
                          control={<Radio size="small" sx={{ color: "#94a3b8", "&.Mui-checked": { color } }} />}
                          label={<RadioLabel style={{ color: ls.status_val === st.value ? color : undefined }}>{st.title}</RadioLabel>}
                        />
                      );
                    })}
                  </RadioGroup>

                  <TextField
                    multiline minRows={2} maxRows={4}
                    placeholder="Add a message to the student (optional)..."
                    value={ls.comments}
                    onChange={(e) => setComment(r.id, e.target.value)}
                    variant="outlined" fullWidth
                    InputProps={{ style: { color: "#e2e8f0", fontSize: 13, background: "rgba(255,255,255,0.03)" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                        "&.Mui-focused fieldset": { borderColor: statusColor(ls.status_val) },
                      },
                    }}
                  />
                </StudentRow>
              );
            })}
          </div>
        )}
      </DialogContent>

      <DialogActions
        style={{
          padding: "12px 24px",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 13, color: "#94a3b8" }}>
          {saveSuccess && !saving && <span style={{ color: "#86efac" }}>&#10003; Saved successfully</span>}
          {saveError && <span style={{ color: "#fca5a5" }}>{saveError}</span>}
          {!saveSuccess && !saveError && dirtyCount > 0 && (
            <span>{dirtyCount} record{dirtyCount !== 1 ? "s" : ""} with unsaved changes</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <MuiButton onClick={onClose} disabled={saving} sx={{ color: "#94a3b8", textTransform: "none" }}>
            Close
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleSave}
            disabled={saving || records.length === 0}
            sx={{
              background: "linear-gradient(135deg, #d97706, #b45309)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              "&:hover": { background: "linear-gradient(135deg, #b45309, #92400e)" },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            {saving ? <><CircularProgress size={16} style={{ color: "#fff", marginRight: 8 }} />Saving&hellip;</> : "Save"}
          </MuiButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}
